import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import { createPortal } from 'react-dom'

function clampContextMenuPosition(
  x: number,
  y: number,
  width: number,
  height: number,
): { x: number; y: number } {
  const padding = 8
  const maxX = Math.max(padding, window.innerWidth - width - padding)
  const maxY = Math.max(padding, window.innerHeight - height - padding)
  return {
    x: Math.min(Math.max(x, padding), maxX),
    y: Math.min(Math.max(y, padding), maxY),
  }
}

/**
 * One debounced-save text field. Saves 500ms after the last keystroke, on blur, and on an
 * explicit flush (escape / click-outside / scroll). A save that lands while the text has moved
 * on re-runs once the in-flight request resolves.
 */
function useDebouncedSaveField(initial: string, onSave?: (text: string) => Promise<void>) {
  const [value, setValue] = useState(initial)
  const valueRef = useRef(initial)
  const lastSavedRef = useRef(initial.trim())
  const dirtyRef = useRef(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const savingRef = useRef(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
    }
  }, [])

  const flush = useCallback(async () => {
    if (!onSave || savingRef.current || !dirtyRef.current || !mountedRef.current) {
      return
    }
    savingRef.current = true
    const payload = valueRef.current
    const savedTrimmed = payload.trim()
    let needsFollowUp = false
    try {
      await onSave(payload)
      if (!mountedRef.current) {
        return
      }
      lastSavedRef.current = savedTrimmed
      needsFollowUp = valueRef.current.trim() !== savedTrimmed
      dirtyRef.current = needsFollowUp
    } finally {
      savingRef.current = false
      if (mountedRef.current && needsFollowUp) {
        void flush()
      }
    }
  }, [onSave])

  const handleChange = useCallback(
    (next: string) => {
      valueRef.current = next
      setValue(next)
      dirtyRef.current = next.trim() !== lastSavedRef.current
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      debounceRef.current = setTimeout(() => {
        void flush()
      }, 500)
    },
    [flush],
  )

  const handleBlur = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    void flush()
  }, [flush])

  return { value, handleChange, handleBlur, flush }
}

export type NotePopoverProps = {
  cellKey: string
  initialText: string
  position: { x: number; y: number }
  onClose: () => void
  onSave: (text: string) => Promise<void>
  /** `cell` = single note textarea (month cells). `line` = note + projection methodology. */
  variant?: 'cell' | 'line'
  initialMethodology?: string
  /** Read-only driver/recipe summary shown above the methodology field, e.g. "Flat 26.3%". */
  computedMethodology?: string | null
  onSaveMethodology?: (text: string) => Promise<void>
  readOnly?: boolean
}

export default function NotePopover({
  cellKey,
  initialText,
  position,
  onClose,
  onSave,
  variant = 'cell',
  initialMethodology = '',
  computedMethodology = null,
  onSaveMethodology,
  readOnly = false,
}: NotePopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [clampedPosition, setClampedPosition] = useState(position)
  const fieldId = useId()
  const isLine = variant === 'line'

  const {
    value: noteText,
    handleChange: handleNoteChange,
    handleBlur: handleNoteBlur,
    flush: flushNote,
  } = useDebouncedSaveField(initialText, readOnly ? undefined : onSave)
  const {
    value: methodologyText,
    handleChange: handleMethodologyChange,
    handleBlur: handleMethodologyBlur,
    flush: flushMethodology,
  } = useDebouncedSaveField(initialMethodology, readOnly ? undefined : onSaveMethodology)

  useEffect(() => {
    const panel = panelRef.current
    if (!panel) {
      setClampedPosition(position)
      return
    }
    const rect = panel.getBoundingClientRect()
    setClampedPosition(clampContextMenuPosition(position.x, position.y, rect.width, rect.height))
  }, [position])

  useEffect(() => {
    if (!readOnly) {
      textareaRef.current?.focus()
    }
  }, [cellKey, readOnly])

  const flushAll = useCallback(async () => {
    await Promise.all([flushNote(), flushMethodology()])
  }, [flushNote, flushMethodology])

  const handleClose = useCallback(() => {
    void flushAll().then(() => onClose())
  }, [flushAll, onClose])

  useEffect(() => {
    function handlePointerDown(event: Event) {
      const target = event.target
      if (target instanceof Node && panelRef.current?.contains(target)) return
      void flushAll().then(() => onClose())
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        void flushAll().then(() => onClose())
      }
    }

    function handleScroll(event: Event) {
      const target = event.target
      if (target instanceof Node && panelRef.current?.contains(target)) return
      void flushAll().then(() => onClose())
    }

    window.addEventListener('mousedown', handlePointerDown)
    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('scroll', handleScroll, true)
    return () => {
      window.removeEventListener('mousedown', handlePointerDown)
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [flushAll, onClose])

  const summary = computedMethodology?.trim()

  return createPortal(
    <div
      ref={panelRef}
      className={`fixed z-50 rounded-md border border-gray-200 bg-white shadow-lg ${
        isLine ? 'w-80 p-3' : 'w-64 p-2'
      }`}
      style={{ left: clampedPosition.x, top: clampedPosition.y }}
      onClick={(e) => e.stopPropagation()}
    >
      <Button
        type="button"
        variant="ghost"
        size="sm"
        iconOnly
        aria-label="Close note"
        className="absolute right-1 top-1"
        onClick={handleClose}
      >
        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
      </Button>
      {isLine ? (
        <>
          <label className="mb-1 block pr-6 text-xs font-medium text-gray-700" htmlFor={`${fieldId}-note`}>
            Note
          </label>
          <textarea
            id={`${fieldId}-note`}
            ref={textareaRef}
            className="input box-border min-h-[6rem] w-full resize-y text-sm"
            rows={3}
            value={noteText}
            readOnly={readOnly}
            onChange={readOnly ? undefined : (e) => handleNoteChange(e.target.value)}
            onBlur={readOnly ? undefined : handleNoteBlur}
            placeholder="Optional commentary"
            aria-label="Line note"
          />
          <div className="mt-3 border-t border-gray-200 pt-3">
            <label
              className="block text-xs font-medium text-gray-700"
              htmlFor={`${fieldId}-methodology`}
            >
              Projection methodology
            </label>
            {summary ? (
              <div className="mt-1.5 rounded border border-gray-200 bg-gray-50 px-2 py-1.5">
                <p className="text-sm text-gray-700">{summary}</p>
              </div>
            ) : null}
            <textarea
              id={`${fieldId}-methodology`}
              className="input box-border mt-1.5 min-h-[6rem] w-full resize-y text-sm"
              rows={3}
              value={methodologyText}
              readOnly={readOnly}
              onChange={readOnly ? undefined : (e) => handleMethodologyChange(e.target.value)}
              onBlur={readOnly ? undefined : handleMethodologyBlur}
              placeholder="How this line is projected"
              aria-label="Projection methodology"
            />
          </div>
        </>
      ) : (
        <textarea
          ref={textareaRef}
          className="input box-border min-h-[6rem] w-full resize-y pr-6 text-sm"
          rows={3}
          value={noteText}
          readOnly={readOnly}
          onChange={readOnly ? undefined : (e) => handleNoteChange(e.target.value)}
          onBlur={readOnly ? undefined : handleNoteBlur}
          aria-label="Cell note"
        />
      )}
    </div>,
    document.body,
  )
}
